import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, Box, Flex, Select, SelectOptionProps, Text, Link, Popover } from '@televet/kibble-ui';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useGetClinicChannelStatusesQuery } from 'shared/types/graphql';
import { NavLink } from 'react-router-dom';

interface IStatusChangeSelectProps {
  defaultStatusName?: string;
  selectedStatusId?: string;
  includeLabel?: boolean;
  handleChange: (newValue: string) => void;
}

interface SwatchProps {
  bgColor?: string;
  borderColor?: string;
}

const Swatch = ({ bgColor = 'transparent', borderColor = 'transparent' }: SwatchProps): JSX.Element => {
  return <Box borderRadius="base" w={4} h={4} bg={bgColor} borderColor={borderColor} border="1px" />;
};

const StatusChangeSelect = ({
  defaultStatusName,
  selectedStatusId,
  includeLabel = true,
  handleChange,
}: IStatusChangeSelectProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();

  const { data: channelStatusesData, refetch: refetchChannelStatuses } = useGetClinicChannelStatusesQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  // refetch statuses if new status is added and set as default
  useEffect(() => {
    refetchChannelStatuses();
  }, [selectedStatusId, refetchChannelStatuses]);

  const channelStatusOptions = useMemo((): SelectOptionProps[] => {
    return (
      channelStatusesData?.findManyChannelStatus?.map((status) => {
        return {
          label: status.name,
          value: status.id,
          leftElement: <Swatch bgColor={status.color} />,
          groupLabel: status.channelStatusAction,
        };
      }) || []
    );
  }, [channelStatusesData?.findManyChannelStatus]);

  const selectedStatus = useMemo(() => {
    if (selectedStatusId) {
      return channelStatusOptions.find((option) => option.value === selectedStatusId);
    }
    return channelStatusOptions.find((option) => option.label === defaultStatusName);
  }, [channelStatusOptions, defaultStatusName, selectedStatusId]);

  const handleStatusChange = useCallback(
    (selectedItem: SelectOptionProps) => {
      const newStatusId = selectedItem.value;
      if (newStatusId) {
        handleChange(newStatusId === 'noChange' ? '' : newStatusId);
      }
    },
    [handleChange],
  );

  return (
    <Box className="compose__status" mt={4}>
      {includeLabel && (
        <Flex className="compose__status-label" alignItems="center" mb={2}>
          <Text mr={1}>{`Status Change (Optional)`}</Text>
          <Popover
            trigger="hover"
            anchor={<Button iconName="infoCircle" size="sm" p={0} variant="ghostNeutral" h="16px" minW="16px" />}
          >
            <Text variant="onContrast">
              This status is assigned to the conversation after a client submits a survey response. Conversation
              statuses can be viewed and edited in
            </Text>
            <Link as={NavLink} to={'/settings/clinic/conversations'} variant="interactiveOnContrast">
              Conversations
            </Link>
          </Popover>
        </Flex>
      )}
      <Select
        placeholder="No status selected"
        value={selectedStatus?.value || 'noChange'}
        options={[
          {
            label: 'No change',
            value: 'noChange',
            groupLabel: 'Active',
            leftElement: <Swatch borderColor="border.default" />,
            isSelected: true,
          },
          ...channelStatusOptions,
        ]}
        listProps={{
          isGrouped: true,
          onSelect: handleStatusChange,
          isSearchable: true,
          maxH: '300px',
          zIndex: 10,
        }}
        bg="background.subtle"
        className="compose__status-select"
      />
    </Box>
  );
};

export default StatusChangeSelect;
